import { useMemo } from "react";
import { ArrowHelper, type Vector3 } from "three";

/** Arrow size constants */
const ARROW_LENGTH = 50;
const ARROW_HEAD_LENGTH = 16;
const ARROW_HEAD_WIDTH = 12; // Bigger arrow head base
const ARROW_OUTSET = 15;
const ARROW_COLOR = 0x00aaff;

/**
 * Helper component to render a 3D arrow pointing in a given direction.
 * Uses Three.js ArrowHelper for correct orientation.
 */
export const Arrow3D = ({
  direction,
  outset = ARROW_OUTSET,
  length = ARROW_LENGTH,
  headLength = ARROW_HEAD_LENGTH,
  headWidth = ARROW_HEAD_WIDTH,
}: {
  direction: Vector3;
  outset?: number;
  length?: number;
  headLength?: number;
  headWidth?: number;
}) => {
  const arrow = useMemo(() => {
    const dir = direction.clone().normalize();
    const origin = dir.clone().multiplyScalar(outset);
    const arrowHelper = new ArrowHelper(
      dir,
      origin,
      length,
      ARROW_COLOR,
      headLength,
      headWidth,
    );
    // Render on top and make semi-transparent
    arrowHelper.renderOrder = 1000;
    arrowHelper.line.renderOrder = 1000;
    arrowHelper.cone.renderOrder = 1000;
    // Mark as drag helper to exclude from measurement bounding box
    arrowHelper.userData = { isDragHelper: true };
    arrowHelper.line.userData = { isDragHelper: true };
    arrowHelper.cone.userData = { isDragHelper: true };

    const lineMat = arrowHelper.line.material;
    const coneMat = arrowHelper.cone.material;
    if (!Array.isArray(lineMat)) {
      lineMat.depthTest = false;
      lineMat.transparent = true;
      lineMat.opacity = 0.85;
    }
    if (!Array.isArray(coneMat)) {
      coneMat.depthTest = false;
      coneMat.transparent = true;
      coneMat.opacity = 0.85;
    }
    return arrowHelper;
  }, [direction, outset, length, headLength, headWidth]);

  return <primitive object={arrow} />;
};
